import { FC } from "react";
import useEvent from "react-use-event-hook";

import { ILocationInfo } from "@ryde/shared";

import {
  MapLibreAutoPanPopup,
  MapLibreStationPinMarker,
} from "../../../common/map/maplibre";
import { IPassengerAddressPin } from "./passenger-address.constants";

interface IPassengerAddressStationPinProps extends IPassengerAddressPin {
  location?: ILocationInfo;
  isPopupOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

/**
 * One of the passenger's stations, with the address popup it opens on click.
 *
 * Which pin is open is owned by the parent, not by each pin, so only one popup can be open at a time —
 * Leaflet allowed a single popup per map, and per-pin state would have let them stack up.
 */
export const PassengerAddressStationPin: FC<
  IPassengerAddressStationPinProps
> = ({
  location,
  type,
  color,
  badgeColor,
  label,
  isPopupOpen,
  onOpen,
  onClose,
}) => {
  // Without this the click also reaches the map, whose click handler is what closes the open popup — so
  // clicking straight from one pin to another would close the popup that is being opened.
  const handleClick = useEvent((event: { originalEvent: MouseEvent }) => {
    event.originalEvent.stopPropagation();
    onOpen();
  });

  if (!location?.latitude || !location?.longitude) {
    return null;
  }

  const { latitude, longitude, fullAddress } = location;

  return (
    <>
      <MapLibreStationPinMarker
        latitude={latitude}
        longitude={longitude}
        type={type}
        color={color}
        badgeColor={badgeColor}
        label={label}
        onClick={handleClick}
        actionLabel={fullAddress}
      />

      <MapLibreAutoPanPopup
        isOpen={isPopupOpen}
        latitude={latitude}
        longitude={longitude}
        onClose={onClose}
      >
        {fullAddress}
      </MapLibreAutoPanPopup>
    </>
  );
};
